const WIDGET_STORE_KEY = 'widget-store';

const KNOWN_TREE_STORE_NAMES = ['explore-a'];

interface WidgetStoreData {
  state: {
    widgets: Record<string, any>;
    updatedAt?: number;
    createdAt?: number;
  };
  version: number;
}

interface TreeStoreData {
  state: {
    container?: string;
    nodes: any[];
    edges: any[];
    edgesPositioning: any[];
    esQuery?: string;
    searchStringNode?: string;
    openAttributeNode?: string;
    updatedAt?: number;
    createdAt?: number;
  };
  version: number;
}

const MAX_AGE_MS = 24 * 60 * 60 * 1000 * 30; // 30 days

export function cleanupWidgetStore(): void {
  try {
    const storedData = localStorage.getItem(WIDGET_STORE_KEY);

    if (!storedData) {
      return;
    }

    const parsedData: WidgetStoreData = JSON.parse(storedData);

    if (!parsedData.state?.widgets) {
      return;
    }

    const storedWidgets = parsedData.state.widgets;
    const widgetIds = Object.keys(storedWidgets);
    const staleWidgetIds: string[] = [];

    widgetIds.forEach((widgetId) => {
      const widget = storedWidgets[widgetId];

      const isStale =
        !widget.updatedAt || Date.now() - widget.updatedAt > MAX_AGE_MS;

      if (isStale) {
        staleWidgetIds.push(widgetId);
      }
    });

    if (staleWidgetIds.length > 0) {
      console.log(
        `Cleaning up ${staleWidgetIds.length} stale widget entries:`,
        staleWidgetIds
      );

      const cleanedWidgets = { ...storedWidgets };
      staleWidgetIds.forEach((id) => {
        delete cleanedWidgets[id];
      });

      const cleanedData: WidgetStoreData = {
        ...parsedData,
        state: {
          ...parsedData.state,
          widgets: cleanedWidgets,
        },
      };

      localStorage.setItem(WIDGET_STORE_KEY, JSON.stringify(cleanedData));
    }
  } catch (error) {
    console.warn('Failed to cleanup widget store:', error);
  }
}

export function cleanupTreeStores(): void {
  try {
    const allKeys = Object.keys(localStorage);
    const now = Date.now();

    const treeStoreKeys = allKeys.filter((key) => {
      if (key === WIDGET_STORE_KEY) {
        return false;
      }

      try {
        const storedData = localStorage.getItem(key);
        if (!storedData) {
          return false;
        }

        const parsedData = JSON.parse(storedData);

        return parsedData.state?.nodes;
      } catch {
        return false;
      }
    });

    if (treeStoreKeys.length === 0) {
      return;
    }

    const staleTreeStores: string[] = [];

    treeStoreKeys.forEach((key) => {
      try {
        const storedData = localStorage.getItem(key);
        if (!storedData) {
          staleTreeStores.push(key);
          return;
        }

        const parsedData: TreeStoreData = JSON.parse(storedData);

        if (!parsedData.state) {
          staleTreeStores.push(key);
          return;
        }

        const storeTimestamp =
          parsedData.state.updatedAt || parsedData.state.createdAt;
        const isTooOld = storeTimestamp && now - storeTimestamp > MAX_AGE_MS;

        if (isTooOld || !storeTimestamp) {
          staleTreeStores.push(key);
        }
      } catch (error) {
        staleTreeStores.push(key);
      }
    });

    if (staleTreeStores.length > 0) {
      console.log(
        `Cleaning up ${staleTreeStores.length} stale tree stores:`,
        staleTreeStores
      );

      staleTreeStores.forEach((key) => {
        localStorage.removeItem(key);
      });
    }
  } catch (error) {
    console.warn('Failed to cleanup tree stores:', error);
  }
}

export function cleanupAllStores(): void {
  cleanupWidgetStore();
  cleanupTreeStores();
}
